import { useClientPortalAPI, useFormikSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { showToast } from "@certn/cascadia";
import {
  BasicButton,
  Card,
  Divider,
  InputButton,
  LinkButton,
  PickerFormikField,
  PrimaryButton,
  Radio,
  RadioGroupFormikField,
  Stack,
  Text,
  ToggleField,
} from "@certn/cascadia-react";
import { FormLabel } from "@certn/cascadia-react/components/inputs/_assets/input-wrapper/FormLabel";
import { css } from "@emotion/css";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mixed, object } from "yup";

import { Schemas } from "../../../types";
import { WorkdayIntegrationHeader } from "./WorkdayIntegrationHeader";
import { WhatsThis } from "../components/WhatsThis";
import { PackageInfo } from "./PackageInfo";

interface Props {
  initialValues:
    | components["schemas"]["AtsIntegrationDetail"]
    | components["schemas"]["AtsIntegrationList"];
  reload: () => void;
}

export function WorkdayIntegration({ initialValues, reload }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: packages, isLoading } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/packages/"
  );

  const { submit, isSubmitting } = useFormikSubmit<
    Schemas["PublicAPIKeyCreateResponse"]
  >(
    `/api/internal/client-portal/ats-integrations/${initialValues.id}/update/`,
    {
      onSuccess: () => {
        showToast(t("Integration updated successfully"), {
          type: "success",
        });
        navigate("/settings/ats-integrations");
      },
      showToastOnError: true,
    }
  );

  const validationSchema = object().shape({
    workday_integration: object().shape({
      packages: mixed()
        .required()
        .label(t("Packages"))
        .test(
          (value) =>
            (value instanceof Array && value.length > 0) ||
            (value instanceof Set && value.size > 0)
        ),
    }),
  });

  return (
    <>
      <Stack gap="space-2" className={styles.stack}>
        <Text style="global/heading-large">{t("Integration details")}</Text>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, helpers) =>
            submit(
              {
                ...values,
                lever_integration: null,
                workday_integration: {
                  ...values.workday_integration,
                  packages: Array.from(values.workday_integration!.packages),
                },
              },
              helpers
            )
          }
          validationSchema={validationSchema}
        >
          {({ values, isValid }) => (
            <Form>
              <WorkdayIntegrationHeader
                initialValues={initialValues}
                reload={reload}
              />
              <Card type="large" className={styles.card}>
                <Stack gap="space-4">
                  <section>
                    <FormLabel
                      isRequired
                      info={
                        <WhatsThis>
                          {t(
                            "Select appropriate certn package(s) to be mapped with Workday's Background check packages"
                          )}
                        </WhatsThis>
                      }
                    >
                      {t("Packages")}
                    </FormLabel>
                    <PickerFormikField
                      items={packages}
                      label={t("Packages")}
                      name="workday_integration.packages"
                      isLoading={isLoading}
                      isDisabled={!initialValues.is_active}
                      selectionMode="multiple"
                      testID="packages-picker"
                      trigger={(props) => (
                        <InputButton
                          rightIcon="expand_more"
                          onToggle={props.onToggle}
                          isDisabled={props.isDisabled}
                        >
                          <Text
                            style="input/text-field"
                            color={
                              props.selectedItems.length < 1
                                ? "text-placeholder"
                                : "text-base"
                            }
                          >
                            {props.selectedItems.length > 1
                              ? t("{{count}} packages selected", {
                                  count: props.selectedItems.length,
                                })
                              : props.selectedItems[0]?.name ||
                                t("Please select...")}
                          </Text>
                        </InputButton>
                      )}
                    />
                  </section>
                  <LinkButton
                    onPress={() => window.open("/packages/create/")}
                    testID="lever-create-package-link"
                  >
                    {t("Create a new package")}
                  </LinkButton>{" "}
                  <Divider />
                  {(packages || [])
                    .filter(({ id }) =>
                      Array.from(
                        values.workday_integration?.packages || []
                      ).includes(id)
                    )
                    .map((packageData, idx) => (
                      <PackageInfo
                        key={packageData.id}
                        packageData={packageData}
                        expanded={idx === 0}
                      />
                    ))}
                  <Text style="global/heading-medium">
                    {t("Advanced settings")}
                  </Text>
                  <section>
                    <RadioGroupFormikField
                      label={t("Report URL type")}
                      testID="report-url-type"
                      name="report_url_type"
                      orientation="horizontal"
                      isDisabled={!initialValues.is_active}
                      isRequired
                    >
                      <Radio testID="direct-pdf-url" value="DIRECT_PDF_URL">
                        {t("PDF URL")}
                      </Radio>
                      <Radio
                        testID="client-portal-url"
                        value="CLIENT_PORTAL_URL"
                      >
                        {t("Client Portal URL (Authenticated)")}
                      </Radio>
                    </RadioGroupFormikField>
                  </section>
                  <section>
                    <FormLabel
                      isRequired
                      info={
                        <WhatsThis>
                          {t(
                            "If enabled, a background check will be added to our system even when the user is not active. In such cases, we will automatically create the user with the minimum role needed to submit the check."
                          )}
                        </WhatsThis>
                      }
                    >
                      {t("User creation")}
                    </FormLabel>
                    <ToggleField
                      label={t("User creation")}
                      name="workday_integration.should_create_users_just_in_time"
                      testID="should_create_users_just_in_time"
                      aria-label="should_create_users_just_in_time"
                      isDisabled={!initialValues.is_active}
                    >
                      {t("Create users just in time")}
                    </ToggleField>
                  </section>
                  <Stack orientation="horizontal" justify="end" gap="space-4">
                    <BasicButton
                      testID="cancel-workday"
                      type="button"
                      isDisabled={isSubmitting}
                      onPress={() => navigate("/settings/ats-integrations")}
                    >
                      {t("Cancel")}
                    </BasicButton>
                    <PrimaryButton
                      testID="save-workday"
                      type="submit"
                      isDisabled={
                        !initialValues.is_active || isSubmitting || !isValid
                      }
                    >
                      {t("Save changes")}
                    </PrimaryButton>
                  </Stack>
                </Stack>
              </Card>
            </Form>
          )}
        </Formik>
      </Stack>
    </>
  );
}

const styles = {
  textFieldSpacing: css({ display: "flex", flex: "1" }),
  stack: css({
    gap: "var(--space-4)",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
    gap: "var(--space-4)",
  }),
};
